import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import EToolTip from 'components/others/EToolTip';
import { useModal } from 'context/ModalContext';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { useToast } from 'context/ToastContext';
import { useNeedPay } from 'modules/admin';
import { openVendorFilterDlg, type SelectOption } from 'modules/product';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { isSameIdSet } from 'utils/helpers/list/isSameIdSet';
import { DEFAULT_SUPPLIER_REQUEST_STATUSES } from './model/constants';
import type { SupplierRequestFilterState } from './model/types';
import SolarIcon from 'components/icons/SolarIcon';

interface SupplierRequestsToolBarProps {
    canOps: boolean;
    filter: SupplierRequestFilterState;
    onReset: () => void;
    onRefresh: () => void;
    onOpenFilter: () => void;
    onVendorSelected: (vendorId: number) => void;
}

const SupplierRequestsToolBar = ({
    canOps,
    filter,
    onReset,
    onRefresh,
    onOpenFilter,
    onVendorSelected,
}: SupplierRequestsToolBarProps) => {
    const { t } = useTranslation(['Warehouses', 'Products', 'common']);
    const { showDlg } = useModal();
    const { localSettings } = useSettings();
    const { darkTheme } = useTheme();
    const { showToast } = useToast();
    const { needPay } = useNeedPay();

    const vendorOptions: SelectOption[] = useMemo(
        () =>
            (localSettings?.DIRS?.Vendor || [])
                .filter((v) => Number(v.ID) > 0)
                .map((v) => ({ value: Number(v.ID), label: String(v.vendorName || `#${v.ID}`) })),
        [localSettings?.DIRS?.Vendor],
    );

    const filterActive =
        filter.vendors.length > 0 || !isSameIdSet(filter.statuses, DEFAULT_SUPPLIER_REQUEST_STATUSES);

    const handleCreate = () => {
        if (needPay()) return;
        openVendorFilterDlg({
            showDlg,
            t: ((key: string, options?: Record<string, unknown>) => {
                const map: Record<string, string> = {
                    selectVendorFilterTitle: t('supplierRequests.selectVendorTitle'),
                    selectVendorFilterLabel: t('supplierRequests.selectVendorLabel'),
                    selectVendorFilterHint: t('supplierRequests.selectVendorHint'),
                    selectAllVendors: t('supplierRequests.selectVendorPlaceholder'),
                };
                if (map[key]) return map[key];
                return t(key as never, options as never);
            }) as typeof t,
            initialVendorId: null,
            vendorOptions,
            selectTheme: darkTheme ? selectStylesDark : selectStylesLigth,
            onApply: (vendorId) => {
                if (!vendorId) {
                    showToast(t('supplierRequests.messVendorRequired'), 'warning');
                    return;
                }
                onVendorSelected(vendorId);
            },
        });
    };

    return (
        <div className="mb-3" style={{ minWidth: '100px' }}>
            <div className="page-toolbar-actions">
                {canOps && (
                    <Button
                        className="fs-14 ms-1"
                        size="sm"
                        variant="success"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleCreate();
                        }}
                    >
                        <SolarIcon name="plus" className="fs-14 p-0" />
                        <span className="d-none d-md-inline ms-1">{t('supplierRequests.toolCreate')}</span>
                    </Button>
                )}

                <ListToolBarActions onReset={onReset} onRefresh={onRefresh} />

                <EToolTip title={t('supplierRequests.toolFilter')}>
                    <span className="d-inline-block ms-1">
                        <Button
                            className="fs-14 px-2 toolbar-icon-btn"
                            size="sm"
                            variant={filterActive ? 'danger' : 'primary'}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                onOpenFilter();
                            }}
                        >
                            <SolarIcon name="funnel" className="fs-14 p-0" />
                        </Button>
                    </span>
                </EToolTip>
            </div>
        </div>
    );
};

export default SupplierRequestsToolBar;
